import { 
  Student, 
  Class, 
  AttendanceRecord, 
  SavedFilter,
  BehaviorIncident,
  TeacherAttendance,
  HomeworkAssignment
} from '../types';

// API base URL - configurable via env with sensible fallback
const API_BASE_URL =
  (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_API_BASE_URL) ||
  (typeof process !== 'undefined' && (process as any).env && (process as any).env.VITE_API_BASE_URL) ||
  'https://class.hiresuperior.com/api';

// Helper function for API calls
const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
    console.log('Making API call to:', url);
  }
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include cookies for session-based auth
    ...options,
  };

  try {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.log(`Making API call to: ${url}`, config);
    }
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
        console.error(`API call failed for ${endpoint}:`, response.status, errorData);
      }
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.log(`API call successful for ${endpoint}:`, result);
    }
    return result;
  } catch (error) {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.error(`API call failed for ${endpoint}:`, error);
    }
    throw error;
  }
};

// Auth service
export const authService = {
  async login(email: string, password: string): Promise<{ id: string; email: string; name: string }> {
    return apiCall<{ id: string; email: string; name: string }>('/login.php', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async logout(): Promise<void> {
    await apiCall('/logout.php', {
      method: 'POST',
    });
  },

  async getCurrentUser(): Promise<{ id: string; email: string; name: string; createdAt: string }> {
    return apiCall<{ id: string; email: string; name: string; createdAt: string }>('/me.php');
  },
};

// Student operations
export const studentService = {
  async create(student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>): Promise<any> {
    const result = await apiCall<any>('/students.php', {
      method: 'POST',
      body: JSON.stringify(student),
    });
    return result;
  },

  async getAll(): Promise<Student[]> {
    return await apiCall<Student[]>('/students.php');
  },

  async getByClass(classId: string): Promise<Student[]> {
    return await apiCall<Student[]>(`/students.php/class/${classId}`);
  },

  async getById(id: string): Promise<Student> {
    return await apiCall<Student>(`/students.php/${id}`);
  },

  async update(id: string, updates: Partial<Student>): Promise<void> {
    await apiCall(`/students.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    console.log('StudentService.delete called with ID:', id);
    await apiCall(`/students.php/${id}`, {
      method: 'DELETE',
    });
  },

  async getClasses(id: string): Promise<Class[]> {
    return await apiCall<Class[]>(`/students/${id}/classes`);
  },

  async uploadPhoto(studentId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/photo`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }
    
    const result = await response.json();
    return result.photoUrl;
  },
};

// Class operations
export const classService = {
  async create(classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const result = await apiCall<{ id: string }>('/classes.php', {
      method: 'POST',
      body: JSON.stringify(classData),
    });
    return result.id;
  },

  async getAll(): Promise<Class[]> {
    return await apiCall<Class[]>('/classes.php');
  },

  async getById(id: string): Promise<Class> {
    return await apiCall<Class>(`/classes.php/${id}`);
  },

  async update(id: string, updates: Partial<Class>): Promise<void> {
    await apiCall(`/classes.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`/classes.php/${id}`, {
      method: 'DELETE',
    });
  },

  async getStudents(id: string): Promise<Student[]> {
    return await apiCall<Student[]>(`/classes.php/${id}/students`);
  },

  async addStudent(classId: string, studentId: string): Promise<void> {
    await apiCall(`/classes.php/${classId}/students`, {
      method: 'POST',
      body: JSON.stringify({ studentId }),
    });
  },

  async removeStudent(classId: string, studentId: string): Promise<void> {
    await apiCall(`/classes.php/${classId}/students/${studentId}`, {
      method: 'DELETE',
    });
  },
};

// Attendance operations
export const attendanceService = {
  async create(attendance: Omit<AttendanceRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const result = await apiCall<{ id: string }>('/attendance.php', {
      method: 'POST',
      body: JSON.stringify(attendance),
    });
    return result.id;
  },

  async getByDateAndClass(date: string, classId: string): Promise<AttendanceRecord[]> {
    return await apiCall<AttendanceRecord[]>(`/attendance.php?date=${date}&classId=${classId}`);
  },

  async getByStudent(studentId: string, startDate?: string, endDate?: string): Promise<AttendanceRecord[]> {
    let url = `/attendance.php?studentId=${studentId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return await apiCall<AttendanceRecord[]>(url);
  },

  async getAll(): Promise<AttendanceRecord[]> {
    return await apiCall<AttendanceRecord[]>('/attendance.php');
  },

  async getById(id: string): Promise<AttendanceRecord> {
    return await apiCall<AttendanceRecord>(`/attendance.php/${id}`);
  },

  async update(id: string, updates: Partial<AttendanceRecord>): Promise<void> {
    await apiCall(`/attendance.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`/attendance.php/${id}`, {
      method: 'DELETE',
    });
  },

  async bulkCreate(attendanceRecords: Omit<AttendanceRecord, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<void> {
    await apiCall('/attendance.php/bulk', {
      method: 'POST',
      body: JSON.stringify({ records: attendanceRecords }),
    });
  },

  async getStats(classId: string, date: string): Promise<any> {
    return await apiCall(`/attendance.php/stats/${classId}/${date}`);
  },
};

// Behavior operations
export const behaviorService = {
  async create(incident: Omit<BehaviorIncident, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const result = await apiCall<{ id: string }>('/behavior.php', {
      method: 'POST',
      body: JSON.stringify(incident),
    });
    return result.id;
  },

  async getAll(): Promise<BehaviorIncident[]> {
    return await apiCall<BehaviorIncident[]>('/behavior.php');
  },

  async getByStudent(studentId: string, startDate?: string, endDate?: string): Promise<BehaviorIncident[]> {
    let url = `/behavior.php?studentId=${studentId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return await apiCall<BehaviorIncident[]>(url);
  },

  async getByClass(classId: string, startDate?: string, endDate?: string): Promise<BehaviorIncident[]> {
    let url = `/behavior.php/class/${classId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;
    return await apiCall<BehaviorIncident[]>(url);
  },

  async getById(id: string): Promise<BehaviorIncident> {
    return await apiCall<BehaviorIncident>(`/behavior.php/${id}`);
  },

  async update(id: string, updates: Partial<BehaviorIncident>): Promise<void> {
    await apiCall(`/behavior.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`/behavior.php/${id}`, {
      method: 'DELETE',
    });
  },
};

// Teacher attendance operations
export const teacherAttendanceService = {
  async create(attendance: Omit<TeacherAttendance, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const result = await apiCall<{ id: string }>('/teacher-attendance.php', {
      method: 'POST',
      body: JSON.stringify(attendance),
    });
    return result.id;
  },

  async getAll(): Promise<TeacherAttendance[]> {
    return await apiCall<TeacherAttendance[]>('/teacher-attendance.php');
  },

  async getById(id: string): Promise<TeacherAttendance> {
    return await apiCall<TeacherAttendance>(`/teacher-attendance.php/${id}`);
  },

  async update(id: string, updates: Partial<TeacherAttendance>): Promise<void> {
    await apiCall(`/teacher-attendance.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`/teacher-attendance.php/${id}`, {
      method: 'DELETE',
    });
  },
};


// Filter operations
export const filterService = {
  async create(filter: Omit<SavedFilter, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const result = await apiCall<{ id: string }>('/filters.php', {
      method: 'POST',
      body: JSON.stringify(filter),
    });
    return result.id;
  },

  async getAll(): Promise<SavedFilter[]> {
    return await apiCall<SavedFilter[]>('/filters.php');
  },

  async getById(id: string): Promise<SavedFilter> {
    return await apiCall<SavedFilter>(`/filters.php/${id}`);
  },

  async update(id: string, updates: Partial<SavedFilter>): Promise<void> {
    await apiCall(`/filters.php/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(id: string): Promise<void> {
    await apiCall(`/filters.php/${id}`, {
      method: 'DELETE',
    });
  },
};

// Export the apiCall function for use in other services
export { apiCall };

// Homework operations
export const homeworkService = {
  async getAll(): Promise<HomeworkAssignment[]> {
    return await apiCall<HomeworkAssignment[]>('/homework.php');
  },

  async getForStudent(studentId: string): Promise<{ assignments: HomeworkAssignment[]; submissions: any[] }> {
    return await apiCall(`/homework.php?student_id=${encodeURIComponent(studentId)}`);
  },

  async getCurrentStudentHomework(): Promise<{ assignments: HomeworkAssignment[]; submissions: any[] }> {
    return await apiCall('/homework.php');
  },

  async create(payload: {
    title: string;
    classId: string;
    dueDate: string; // ISO string
    description?: string;
    maxPoints?: number;
    instructions?: string;
  }): Promise<{ id: string; message: string }> {
    return await apiCall('/homework.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async update(id: string, updates: Partial<HomeworkAssignment>): Promise<{ message: string }> {
    return await apiCall('/homework.php', {
      method: 'PUT',
      body: JSON.stringify({ id, ...updates }),
    });
  },

  async delete(id: string): Promise<{ message: string }> {
    return await apiCall('/homework.php', {
      method: 'DELETE',
      body: JSON.stringify({ id }),
    });
  },
};

// Homework submission operations
export const homeworkSubmissionService = {
  async getForAssignment(assignmentId: string): Promise<any[]> {
    return await apiCall<any[]>(`/homework-submissions.php?assignment_id=${encodeURIComponent(assignmentId)}`);
  },

  async getCurrentStudentSubmissions(): Promise<any[]> {
    return await apiCall<any[]>('/homework-submissions.php');
  },

      async submit(assignmentId: string, content: string, attachments?: File[]): Promise<{ id: string; message: string }> {
        // Convert files to base64 for storage
        const attachmentData: string[] = [];
        const attachmentNames: string[] = [];
        
        if (attachments && attachments.length > 0) {
          for (const file of attachments) {
            attachmentNames.push(file.name);
            try {
              const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });
              attachmentData.push(base64);
            } catch (error) {
              console.error('Error reading file:', error);
              attachmentData.push(''); // Empty string for failed files
            }
          }
        }
        
        return await apiCall('/homework-submissions.php', {
          method: 'POST',
          body: JSON.stringify({ 
            assignmentId, 
            content, 
            attachments: attachmentNames,
            attachmentData: attachmentData
          }),
        });
      },

  async grade(submissionId: string, points: number, feedback: string): Promise<{ message: string }> {
    return await apiCall('/homework-submissions.php', {
      method: 'PUT',
      body: JSON.stringify({ id: submissionId, points, feedback }),
    });
  },
};
